/*
 * PuriPet slide cart (replaces the Qikify Slide Cart app).
 *
 * Markup: snippets/cart-drawer.liquid, re-rendered through the Section Rendering API
 * (section "cart-drawer") after every cart change.
 *
 * Public API kept from the original Flux drawer, because product-form.js, quick-add.js
 * and quick-order-list.js call it: open(), close(), renderContents(parsedState),
 * getSectionsToRender(), getSectionInnerHTML(), setActiveElement().
 *
 * Cart changes made by apps that post to /cart/* themselves (Seal Subscriptions,
 * ShineTrust, ...) are picked up with a PerformanceObserver, so the drawer stays in sync
 * and opens after an add without patching window.fetch. Adds whose URL carries
 * "pp_silent=1" (e.g. the PDP add-on tiles) refresh the drawer without opening it.
 */
/*
 * Upsell tracking. When a product is added through an upsell button, the button's name and
 * the product are appended to the hidden cart attribute "_upsell". Cart attributes are copied
 * onto the order, so every order shows which upsells it came with. The leading underscore keeps
 * the attribute out of the customer's checkout. A Shopify custom event is published as well,
 * for analytics pixels.
 */
window.PuriPetUpsellTracker = window.PuriPetUpsellTracker || {
  key: '_upsell',
  queue: Promise.resolve(),

  /** @param {string} source  e.g. "Warenkorb", "Perfekt dazu", "Vorteilspack"
   *  @param {Array} items     line items from the /cart/add response (product_title, variant_title) */
  track(source, items) {
    const lines = (items || []).filter(Boolean);
    if (!source || lines.length === 0) return;
    const entries = lines.map((item) => {
      const variant = item.variant_title ? ` (${item.variant_title})` : '';
      return `${source}: ${item.product_title}${variant}`;
    });

    // Serialised, so two quick clicks never overwrite each other.
    this.queue = this.queue
      .then(() => fetch(`${routes.cart_url}.js`).then((response) => response.json()))
      .then((cart) => {
        const existing = (cart.attributes && cart.attributes[this.key]) || '';
        const value = [existing, ...entries].filter(Boolean).join('\n');
        return fetch(`${routes.cart_update_url}.js?pp_track=1`, {
          ...fetchConfig('json'),
          body: JSON.stringify({ attributes: { [this.key]: value } }),
        });
      })
      .catch((error) => console.error(error));

    if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
      lines.forEach((item) =>
        Shopify.analytics.publish('puripet_upsell_added', {
          source,
          product: item.product_title,
          variant: item.variant_title || '',
          variant_id: item.variant_id,
        })
      );
    }
  },
};

class CartDrawer extends HTMLElement {
  static SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  static CART_REQUEST = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.lastRender = 0;

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this));
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());

    this.bindCartIcon();
    this.observeCartRequests();
    this.setupCarousel();
  }

  /* ---------- open / close ---------- */

  bindCartIcon() {
    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest('#cart-icon-bubble');
      if (!cartLink || this.isCartPage()) return;
      event.preventDefault();
      this.open(cartLink);
    });
    this.updateCartIconAccessibility();
  }

  updateCartIconAccessibility() {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  isCartPage() {
    return Boolean(document.querySelector('cart-items'));
  }

  open(triggeredBy) {
    if (this.isCartPage()) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.classList.add('active');
    // Sizes are only measurable once the panel is visible.
    requestAnimationFrame(() => this.updateCarousel());
    document.documentElement.classList.add('pp-cart-open');
    this.trapFocus();
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    document.documentElement.classList.remove('pp-cart-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
  }

  trapFocus() {
    const panel = this.querySelector('.pp-cart__panel');
    if (panel && typeof trapFocus === 'function') trapFocus(panel, panel);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ---------- rendering ---------- */

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  // Called by product-form.js and quick-add.js with their /cart/add response.
  renderContents(parsedState) {
    this.productId = parsedState && parsedState.id;
    if (parsedState && parsedState.sections) {
      this.renderSections(parsedState.sections);
    } else {
      this.refresh();
    }
    setTimeout(() => this.open());
  }

  renderSections(sections) {
    if (!sections) return;
    this.lastRender = performance.now();
    // The body scrolls normally; on short screens the whole panel scrolls instead. Keep both.
    const scrollers = ['.pp-cart__body', '.pp-cart__panel'].map((selector) => {
      const element = this.querySelector(selector);
      return [selector, element ? element.scrollTop : 0];
    });
    const openDetails = Array.from(this.querySelectorAll('details[open] summary')).map((summary) => summary.textContent.trim());

    this.getSectionsToRender().forEach((section) => {
      const html = sections[section.id];
      if (!html) return;
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (target) target.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    const count = Number(this.querySelector('[data-cart-count]')?.dataset.cartCount || 0);
    this.classList.toggle('is-empty', count === 0);
    this.querySelectorAll('details summary').forEach((summary) => {
      if (openDetails.includes(summary.textContent.trim())) summary.parentElement.open = true;
    });
    scrollers.forEach(([selector, scrollTop]) => {
      const element = this.querySelector(selector);
      if (element) element.scrollTop = scrollTop;
    });

    this.updateCartIconAccessibility();
    this.setupCarousel();
    if (this.classList.contains('active')) this.trapFocus();
  }

  refresh() {
    const url = `${window.location.pathname}?sections=${CartDrawer.SECTIONS.join(',')}`;
    return fetch(url)
      .then((response) => response.json())
      .then((sections) => this.renderSections(sections))
      .catch((error) => console.error(error));
  }

  /* ---------- cart requests ---------- */

  request(endpoint, body) {
    this.classList.add('is-loading');
    this.showError('');

    return fetch(`${endpoint}.js?pp_silent=1`, {
      ...fetchConfig('json'),
      body: JSON.stringify({
        ...body,
        sections: CartDrawer.SECTIONS,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message || window.cartStrings?.error);
        this.renderSections(response.sections);
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: response });
        return response;
      })
      .catch((error) => {
        this.showError(error.message || window.cartStrings?.error);
        throw error;
      })
      .finally(() => this.classList.remove('is-loading'));
  }

  changeLine(key, quantity, extra = {}) {
    return this.request(routes.cart_change_url, { id: key, quantity, ...extra });
  }

  /* ---------- events ---------- */

  onClick(event) {
    if (event.target.closest('[data-cart-close]')) {
      this.close();
      return;
    }

    const arrow = event.target.closest('[data-cart-scroll]');
    if (arrow) {
      const track = this.querySelector('[data-cart-track]');
      const card = track && track.firstElementChild;
      if (card) track.scrollBy({ left: Number(arrow.dataset.cartScroll) * (card.offsetWidth + 10), behavior: 'smooth' });
      return;
    }

    const button = event.target.closest('[data-cart-action]');
    if (!button) return;
    const item = button.closest('[data-key]');
    const quantityInput = item && item.querySelector('[data-cart-quantity]');
    const quantity = quantityInput ? Number(quantityInput.value) : 0;
    const step = quantityInput ? Number(quantityInput.step) || 1 : 1;

    switch (button.dataset.cartAction) {
      case 'increase':
        this.changeLine(item.dataset.key, quantity + step).catch(() => {});
        break;
      case 'decrease':
        this.changeLine(item.dataset.key, Math.max(quantity - step, 0)).catch(() => {});
        break;
      case 'remove':
        this.changeLine(item.dataset.key, 0).catch(() => {});
        break;
      case 'subscribe': {
        const plan = item.querySelector('[data-cart-plan]');
        this.changeLine(item.dataset.key, quantity, { selling_plan: Number(plan.value) }).catch(() => {});
        break;
      }
      case 'upsell': {
        const card = button.closest('.pp-cart__upsell');
        const select = card.querySelector('[data-cart-upsell-variant]');
        const id = Number(select ? select.value : button.dataset.variantId);
        const source = button.closest('[data-upsell-source]')?.dataset.upsellSource || 'Warenkorb';
        this.request(routes.cart_add_url, { items: [{ id, quantity: 1 }] })
          .then((response) => window.PuriPetUpsellTracker.track(source, response.items))
          .catch(() => {});
        break;
      }
      case 'remove-code': {
        const code = button.closest('[data-code]').dataset.code;
        this.updateDiscounts(this.getDiscountCodes().filter((existing) => existing !== code));
        break;
      }
    }
  }

  onChange(event) {
    const input = event.target;
    if (input.matches('[data-cart-upsell-variant]')) {
      const card = input.closest('.pp-cart__upsell');
      const template = card.querySelector(`[data-cart-upsell-price-for="${input.value}"]`);
      if (template) card.querySelector('[data-cart-upsell-price]').innerHTML = template.innerHTML;
    } else if (input.matches('[data-cart-quantity]')) {
      clearTimeout(this.quantityTimer);
      this.quantityTimer = setTimeout(() => {
        const key = input.closest('[data-key]').dataset.key;
        this.changeLine(key, Math.max(Number(input.value) || 0, 0)).catch(() => {});
      }, 400);
    } else if (input.matches('[data-cart-note]')) {
      fetch(`${routes.cart_update_url}.js?pp_silent=1`, {
        ...fetchConfig('json'),
        body: JSON.stringify({ note: input.value }),
      });
    } else if (input.matches('[data-cart-terms]')) {
      const checkout = this.querySelector('[name="checkout"]');
      if (checkout) checkout.disabled = !input.checked;
    }
  }

  onSubmit(event) {
    const discountForm = event.target.closest('[data-cart-discount-form]');
    if (!discountForm) return;
    event.preventDefault();

    const code = discountForm.elements.discount.value.trim();
    if (!code) return;
    const codes = this.getDiscountCodes();
    if (!codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) codes.push(code);

    this.updateDiscounts(codes).then((response) => {
      const applied = response && response.discount_codes;
      const match = applied && applied.find((entry) => entry.code.toLowerCase() === code.toLowerCase());
      if (!match || !match.applicable) this.showError(this.dataset.discountError || '');
    });
  }

  getDiscountCodes() {
    return Array.from(this.querySelectorAll('[data-code]')).map((element) => element.dataset.code);
  }

  updateDiscounts(codes) {
    return this.request(routes.cart_update_url, { discount: codes.join(',') }).catch(() => {});
  }

  showError(message) {
    const error = this.querySelector('[data-cart-error]');
    if (error) error.textContent = message || '';
  }

  /* ---------- upsell carousel (arrows + mouse drag; touch scrolls natively) ---------- */

  setupCarousel() {
    const track = this.querySelector('[data-cart-track]');
    if (!track || track.dataset.ready) return;
    track.dataset.ready = 'true';
    track.addEventListener('scroll', () => this.updateCarousel(), { passive: true });

    let startX = 0;
    let startScroll = 0;
    let moved = false;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('select')) return;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      moved = false;

      const onMove = (moveEvent) => {
        const delta = moveEvent.clientX - startX;
        if (!moved && Math.abs(delta) < 5) return;
        if (!moved) {
          moved = true;
          track.classList.add('is-dragging');
        }
        track.scrollLeft = startScroll - delta;
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (!moved) return;
        // Let the release not count as a click on a card link or button.
        setTimeout(() => track.classList.remove('is-dragging'));
        const card = track.firstElementChild;
        if (card) {
          const step = card.offsetWidth + 10;
          track.scrollTo({ left: Math.round(track.scrollLeft / step) * step, behavior: 'smooth' });
        }
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    });

    this.updateCarousel();
  }

  updateCarousel() {
    const track = this.querySelector('[data-cart-track]');
    if (!track) return;
    const wrapper = track.closest('[data-cart-carousel]');
    const maxScroll = track.scrollWidth - track.clientWidth;
    wrapper.classList.toggle('has-overflow', maxScroll > 4);
    const [prev, next] = wrapper.querySelectorAll('[data-cart-scroll]');
    if (prev) prev.disabled = track.scrollLeft <= 4;
    if (next) next.disabled = track.scrollLeft >= maxScroll - 4;
  }

  /* ---------- cart changes made outside the drawer ---------- */

  observeCartRequests() {
    if (!('PerformanceObserver' in window)) return;
    let shouldOpen = false;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries().filter((entry) => {
        if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return false;
        const url = new URL(entry.name, window.location.origin);
        if (!CartDrawer.CART_REQUEST.test(url.pathname + url.search)) return false;
        if (url.searchParams.has('pp_track')) return false;
        // Skip requests that already rendered the drawer themselves (this drawer, product-form.js).
        return entry.responseEnd > this.lastRender;
      });
      if (entries.length === 0) return;

      if (entries.some((entry) => /\/cart\/add/.test(entry.name) && !/pp_silent=1/.test(entry.name))) shouldOpen = true;
      clearTimeout(this.observerTimer);
      this.observerTimer = setTimeout(() => {
        const open = shouldOpen && this.dataset.openOnAdd === 'true';
        shouldOpen = false;
        this.refresh().then(() => open && this.open());
      }, 150);
    });

    observer.observe({ type: 'resource', buffered: false });
  }
}

customElements.define('cart-drawer', CartDrawer);
